import React, { useCallback, useEffect, useState } from 'react';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import { useSetWizardHeaderAside } from '../../../pages/wizard/WizardHeaderAside';
import useSmartBomVersions from './useSmartBomVersions';
import useSmartBomReveal from './useSmartBomReveal';
import SmartBomButton from './SmartBomButton';
import SmartBomRevealCard from './SmartBomRevealCard';
import SmartBomModal, { SmartBomSheetTab } from './SmartBomModal';
import RfqCriteriaBrief from '../../criteria/RfqCriteriaBrief';

/**
 * The Smart BOM surface of the supplier-discovery sub-step — the ONLY
 * sub-step that shows it, so the reveal is earned rather than ambient.
 * Owns the version-list state and one shared modal for its two entry points:
 *
 * - the reveal moment (once per version state, real numbers) landing on the
 *   Reasoning sheet — the story of how the BOM got smarter;
 * - the quiet pill, the standing reference, landing on the grid.
 *
 * Viewers see everything too (it is a read surface); editing inside the
 * modal still follows the editor role.
 */
export interface SmartBomDiscoveryPanelProps {
  bomId: string | null;
}

export const SmartBomDiscoveryPanel: React.FC<SmartBomDiscoveryPanelProps> = ({ bomId }) => {
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<SmartBomSheetTab>('smart-bom');
  const readOnly = useReadOnly();
  const state = useSmartBomVersions(bomId);
  const reveal = useSmartBomReveal(bomId, state.versions);

  // Opening the sheet re-fetches the list and lands on the latest version —
  // versions can be minted while the buyer sits on discovery (shortlisting),
  // and the sheet must always open on the BOM as it stands now.
  const refresh = state.refresh;
  const openOn = useCallback((tab: SmartBomSheetTab) => {
    refresh();
    setInitialTab(tab);
    setOpen(true);
  }, [refresh]);

  // The pill lives on the step-header line (right of the subtitle) via the
  // wizard's header-aside slot, so it costs the page no extra row; the slot
  // clears itself when this panel unmounts (leaving the discovery sub-step).
  const setHeaderAside = useSetWizardHeaderAside();
  const latestVersion = state.latestVersion;
  useEffect(() => {
    if (!bomId) return undefined;
    setHeaderAside(
      <SmartBomButton latestVersion={latestVersion} onClick={() => openOn('smart-bom')} />,
    );
    return () => setHeaderAside(null);
  }, [bomId, latestVersion, openOn, setHeaderAside]);

  if (!bomId) return null;

  return (
    <>
      {reveal.show && (
        <SmartBomRevealCard
          latestVersion={reveal.latestVersion}
          changeCount={reveal.changeCount}
          onSee={() => {
            reveal.acknowledge();
            openOn('reasoning');
          }}
          onDismiss={reveal.acknowledge}
          criteria={<RfqCriteriaBrief variant="compact" surface="none" />}
        />
      )}

      <SmartBomModal
        open={open}
        onClose={() => setOpen(false)}
        initialTab={initialTab}
        versions={state.versions}
        selectedVersion={state.selectedVersion}
        detail={state.detail}
        loading={state.loadingList || state.loadingDetail}
        error={state.error}
        canEdit={!readOnly}
        saving={state.saving}
        downloading={state.downloading}
        savedNotice={state.savedNotice}
        onSelectVersion={state.selectVersion}
        onSave={state.saveEdits}
        onDownload={state.download}
        criteria={<RfqCriteriaBrief surface="strip" onAsk={() => setOpen(false)} />}
      />
    </>
  );
};

export default SmartBomDiscoveryPanel;
